/**
 * Local notifications — SPEC §15 / SEC-PUSH-*.
 * Wake-style content by default: no sender, conversation id, or message body
 * unless the user explicitly enables previews (title/body strings only).
 * OS notification `data` ALWAYS carries an opaque wake token — never conversationId.
 */

export type NotificationPermissionState = NotificationPermission | "unsupported";

/** No `document` in workers / tests: treat the page as not visible. */
const pageVisible = (): boolean =>
  typeof document !== "undefined" && document.visibilityState === "visible";

export interface NotificationPrefs {
  enabled: boolean;
  /** SEC-PUSH-004: default false — hide sender/body */
  showPreviews: boolean;
  notifyWhileFocused: boolean;
  sound: boolean;
  /** Banner at the top for messages in other chats while Veto is open (phone parity). Default on. */
  inAppBanners: boolean;
}

const PREFS_KEY = "veto.notification.prefs";
const WAKE_MAP_PREFIX = "veto.wake.";
const WAKE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  enabled: true,
  showPreviews: false,
  notifyWhileFocused: false,
  sound: false,
  inAppBanners: true,
};

export function loadNotificationPrefs(): NotificationPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_NOTIFICATION_PREFS };
    const parsed = JSON.parse(raw) as Partial<Record<keyof NotificationPrefs, unknown>>;
    const flag = (k: keyof NotificationPrefs) =>
      typeof parsed[k] === "boolean" ? (parsed[k] as boolean) : DEFAULT_NOTIFICATION_PREFS[k];
    return {
      enabled: flag("enabled"),
      showPreviews: flag("showPreviews"),
      notifyWhileFocused: flag("notifyWhileFocused"),
      sound: flag("sound"),
      inAppBanners: flag("inAppBanners"),
    };
  } catch {
    return { ...DEFAULT_NOTIFICATION_PREFS };
  }
}

/** One-time "Know when someone writes" sheet (phone parity): remembered once answered. */
const ASKED_KEY = "veto.notify.asked";

export function notificationPromptPending(): boolean {
  if (getNotificationPermission() !== "default") return false;
  try {
    return localStorage.getItem(ASKED_KEY) === null;
  } catch {
    return false;
  }
}

export function markNotificationPromptShown(): void {
  try {
    localStorage.setItem(ASKED_KEY, "1");
  } catch {
    /* private mode — the sheet may show again next time */
  }
}

export function saveNotificationPrefs(prefs: NotificationPrefs): void {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

export function getNotificationPermission(): NotificationPermissionState {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (!("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  const result = await Notification.requestPermission();
  return result;
}

export interface IncomingNotifyInput {
  conversationId: string;
  contactName?: string;
  bodyPreview?: string;
  activeConversationId: string | null;
  prefs: NotificationPrefs;
  /** Locked chat: never show who or what, in the app or on the lock screen (phone parity). */
  locked?: boolean;
}

export interface InAppBannerPayload {
  conversationId: string;
  title: string;
  body: string;
}

function playSoftChime(): void {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = 880;
    gain.gain.value = 0.0001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.exponentialRampToValueAtTime(0.05, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
    osc.start(now);
    osc.stop(now + 0.3);
    osc.onended = () => void ctx.close();
  } catch {
    /* ignore */
  }
}

interface WakeEntry {
  conversationId: string;
  expiresAt: number;
}

function wakeStorage(): Storage {
  // localStorage survives cold openWindow from the service worker (sessionStorage does not).
  return localStorage;
}

export function rememberWakeToken(token: string, conversationId: string): void {
  try {
    const entry: WakeEntry = {
      conversationId,
      expiresAt: Date.now() + WAKE_TTL_MS,
    };
    wakeStorage().setItem(`${WAKE_MAP_PREFIX}${token}`, JSON.stringify(entry));
  } catch {
    /* private mode */
  }
}

/** Resolve opaque wake token → conversation after user unlocks (local only). */
export function resolveWakeToken(token: string): string | null {
  try {
    const raw = wakeStorage().getItem(`${WAKE_MAP_PREFIX}${token}`);
    if (!raw) return null;
    // Legacy plain conversationId string
    if (!raw.startsWith("{")) {
      return raw;
    }
    const entry = JSON.parse(raw) as WakeEntry;
    if (!entry?.conversationId) return null;
    if (typeof entry.expiresAt === "number" && entry.expiresAt < Date.now()) {
      wakeStorage().removeItem(`${WAKE_MAP_PREFIX}${token}`);
      return null;
    }
    return entry.conversationId;
  } catch {
    return null;
  }
}

export function purgeExpiredWakeTokens(): void {
  try {
    const store = wakeStorage();
    const now = Date.now();
    for (let i = store.length - 1; i >= 0; i--) {
      const key = store.key(i);
      if (!key?.startsWith(WAKE_MAP_PREFIX)) continue;
      const raw = store.getItem(key);
      if (!raw) continue;
      if (!raw.startsWith("{")) continue;
      try {
        const entry = JSON.parse(raw) as WakeEntry;
        if (entry.expiresAt < now) store.removeItem(key);
      } catch {
        store.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}

/**
 * Returns an in-app banner payload when appropriate, and fires an OS notification
 * for background / opted-in focused cases.
 *
 * SEC-PUSH-001: OS notification `data`/tag must not embed conversation ids —
 * only an opaque wake token (+ collapse key).
 * SEC-PUSH-004: OS title/body stay opaque unless the user opts into previews.
 * In-app banners MAY show decrypted sender/body while the vault is unlocked
 * (plaintext already in the client).
 */
export async function notifyIncomingMessage(
  input: IncomingNotifyInput,
): Promise<InAppBannerPayload | null> {
  const { prefs } = input;
  // Older saved prefs have no banner flag: banners stay on.
  const bannersOn = prefs.inAppBanners !== false;
  if (!prefs.enabled && !bannersOn) return null;

  const viewingSame = input.activeConversationId === input.conversationId;
  if (viewingSame && pageVisible()) {
    return null;
  }

  // In-app: useful decrypted preview when available (never for a locked chat).
  const inAppTitle = input.locked ? "Locked chat" : input.contactName?.trim() || "Veto";
  const inAppBody = input.locked
    ? "New message"
    : input.bodyPreview?.trim()
      ? truncate(input.bodyPreview, 96)
      : "New encrypted message";

  // OS / lock-screen: opaque by default (SEC-PUSH-004); locked chats always opaque.
  const showPreview = prefs.showPreviews && !input.locked;
  const osTitle = showPreview && input.contactName ? input.contactName : "Veto";
  const osBody =
    showPreview && input.bodyPreview ? truncate(input.bodyPreview, 96) : "New encrypted message";

  const banner: InAppBannerPayload = {
    conversationId: input.conversationId,
    title: inAppTitle,
    body: inAppBody,
  };

  // System notifications: when Veto is in the background, or — opt-in — while it is open too.
  const shouldOsNotify = prefs.enabled && (!pageVisible() || prefs.notifyWhileFocused);

  if (shouldOsNotify && getNotificationPermission() === "granted") {
    const reg = await navigator.serviceWorker?.getRegistration();
    const wakeToken = crypto.randomUUID();
    rememberWakeToken(wakeToken, input.conversationId);
    const options: NotificationOptions & { renotify?: boolean } = {
      body: osBody,
      icon: "/icons/icon-192.png",
      badge: "/icons/icon-192.png",
      tag: `veto-wake-${wakeToken}`,
      renotify: true,
      silent: !prefs.sound,
      // Always opaque — never put conversationId in OS-visible data (SEC-PUSH-001).
      data: { type: "wake", token: wakeToken, collapse: "account-device" },
    };
    try {
      if (reg?.showNotification) {
        await reg.showNotification(osTitle, options);
      } else {
        const n = new Notification(osTitle, options);
        n.onclick = () => {
          window.focus();
          const data = (n as Notification & { data?: { token?: string } }).data;
          const conversationId =
            (data?.token ? resolveWakeToken(data.token) : null) ?? input.conversationId;
          window.dispatchEvent(
            new CustomEvent("veto:open-conversation", {
              detail: { conversationId },
            }),
          );
          n.close();
        };
      }
    } catch {
      /* Notifications can fail on insecure contexts besides localhost */
    }
  }

  // In-app banner (+ the soft chime) while the app is visible but the user is elsewhere.
  if (pageVisible() && !viewingSame && bannersOn) {
    if (prefs.sound) playSoftChime();
    return banner;
  }
  return null;
}

export async function syncAppBadge(unreadTotal: number): Promise<void> {
  try {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (unreadTotal > 0 && nav.setAppBadge) await nav.setAppBadge(unreadTotal);
    else if (nav.clearAppBadge) await nav.clearAppBadge();
  } catch {
    /* unsupported */
  }
}

/** Register Background Sync so outbox retries resume when connectivity returns. */
export async function registerOutboxBackgroundSync(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.ready;
    const syncManager = (
      reg as ServiceWorkerRegistration & {
        sync?: { register: (tag: string) => Promise<void> };
      }
    )?.sync;
    if (syncManager?.register) {
      await syncManager.register("veto-outbox");
    }
  } catch {
    /* unsupported or permission */
  }
}

function truncate(s: string, n: number): string {
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n - 1)}…`;
}
